import React, { useState, useRef } from 'react';
import { Send, Phone, Mail, MapPin, CheckCircle2, ShieldCheck } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { submitInquiry, companyInfo } from '../data/mock';
import Turnstile from './Turnstile';

const countryCodes = [
  { value: '+91', label: 'India (+91)' },
  { value: '+1', label: 'United States (+1)' },
  { value: '+44', label: 'United Kingdom (+44)' },
  { value: '+61', label: 'Australia (+61)' },
  { value: '+971', label: 'UAE (+971)' },
  { value: '+65', label: 'Singapore (+65)' }
];

const validatePhone = (countryCode, phone) => {
  const digits = phone.replace(/\D/g, '');

  switch (countryCode) {
    case '+91':
      return /^[6-9]\d{9}$/.test(digits);
    case '+1':
      return /^\d{10}$/.test(digits);
    case '+44':
      return /^\d{10}$/.test(digits);
    case '+61':
      return /^\d{9}$/.test(digits);
    case '+971':
      return /^\d{9}$/.test(digits);
    case '+65':
      return /^\d{8}$/.test(digits);
    default:
      return digits.length >= 7 && digits.length <= 15;
  }
};

export const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileError, setTurnstileError] = useState(false);
  const turnstileRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+91',
    businessType: '',
    serviceInterest: '',
    budgetRange: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let sanitizedValue;
    if (name === 'phone') {
      sanitizedValue = value.replace(/\D/g, '').slice(0, 10);
    } else if (name === 'name') {
      // disallow digits in name
      sanitizedValue = value.replace(/\d/g, '');
    } else {
      sanitizedValue = value;
    }
    setFormData(prev => ({ ...prev, [name]: sanitizedValue }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validatePhone(formData.countryCode, formData.phone)) {
      toast.error('Please enter a valid phone number for the selected country code.', {
        duration: 5000
      });
      return;
    }

    // Security check: the Turnstile token must be present before we allow submission
    if (!turnstileToken) {
      toast.error('Please complete the security check to continue.', {
        duration: 5000
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await submitInquiry({ ...formData, turnstileToken });
      if (result.success) {
        toast.success("Thank You!", {
          description: result.message,
          duration: 5000,
        });

        // Reset the Turnstile widget so a new token is required for the next submission
        turnstileRef.current?.reset();
        setTurnstileToken('');

        // Clear the form after successful submission
        setFormData({
          name: '',
          email: '',
          phone: '',
          countryCode: '+91',
          businessType: '',
          serviceInterest: '',
          budgetRange: '',
          message: ''
        });
      }
    } catch (error) {
      toast.error("Submission Failed", {
        description: "Something went wrong. Please try again or contact us directly.",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-blue-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Let's <span className="text-blue-600">Grow Together</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Contact us today for a free consultation and discover how we can transform your business
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">Get In Touch</h3>
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                Ready to take your business to the next level? Our team is here to help you create 
                powerful advertising campaigns that deliver real results.
              </p>
            </div>

            {/* Contact Cards */}
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-shadow border-l-4 border-blue-600">
                <div className="flex items-start gap-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <Phone className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Phone</h4>
                    {companyInfo.phone.map((number, index) => (
                      <a 
                        key={index}
                        href={`tel:${number}`}
                        className="text-blue-600 hover:text-blue-700 font-medium block"
                      >
                        {number}
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-shadow border-l-4 border-blue-600">
                <div className="flex items-start gap-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <Mail className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Email</h4>
                    <a 
                      href={`mailto:${companyInfo.email}`}
                      className="text-blue-600 hover:text-blue-700 font-medium break-all block"
                    >
                      {companyInfo.email}
                    </a>
                    <a 
                      href={`mailto:${companyInfo.emailSecondary}`}
                      className="text-blue-600 hover:text-blue-700 font-medium break-all block"
                    >
                      {companyInfo.emailSecondary}
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-shadow border-l-4 border-blue-600">
                <div className="flex items-start gap-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <MapPin className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Address</h4>
                    <p className="text-gray-700">{companyInfo.address}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-shadow border-l-4 border-blue-600">
                <div className="flex items-start gap-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <span className="text-2xl">🌐</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Website</h4>
                    <a 
                      href={`https://${companyInfo.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-700 font-medium"
                    >
                      {companyInfo.website}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Benefits List */}
            <div className="bg-blue-600 rounded-xl p-6 text-white">
              <h4 className="font-bold text-lg mb-4">What You Get:</h4>
              <ul className="space-y-3">
                <li className="flex items-center gap-2">
                  <CheckCircle2 size={20} />
                  <span>Free consultation with our experts</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 size={20} />
                  <span>Custom campaign proposals</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 size={20} />
                  <span>Competitive pricing options</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 size={20} />
                  <span>24/7 customer support</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Send Us Your Inquiry</h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter your full name"
                  className="mt-2"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="you@example.com"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <div className="grid grid-cols-[75px_1fr] gap-3 mt-2">
                    <div>
                      <Select
                        value={formData.countryCode}
                        onValueChange={(value) => handleSelectChange('countryCode', value)}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue asChild>
                            <span>{formData.countryCode || 'Code'}</span>
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {countryCodes.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Enter phone number"
                      maxLength={10}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="businessType">Business Type</Label>
                <Select 
                  value={formData.businessType}
                  onValueChange={(value) => handleSelectChange('businessType', value)}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select your business type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="retail">Retail</SelectItem>
                    <SelectItem value="healthcare">Healthcare</SelectItem>
                    <SelectItem value="education">Education</SelectItem>
                    <SelectItem value="realestate">Real Estate</SelectItem>
                    <SelectItem value="hospitality">Hospitality</SelectItem>
                    <SelectItem value="manufacturing">Manufacturing</SelectItem>
                    <SelectItem value="services">Services</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="serviceInterest">Service Interest</Label>
                <Select 
                  value={formData.serviceInterest}
                  onValueChange={(value) => handleSelectChange('serviceInterest', value)}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="What service are you interested in?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="outdoor">Outdoor Advertising</SelectItem>
                    <SelectItem value="digital">Digital Marketing</SelectItem>
                    <SelectItem value="print">Print Media</SelectItem>
                    <SelectItem value="broadcast">Radio/TV Advertising</SelectItem>
                    <SelectItem value="social">Social Media Marketing</SelectItem>
                    <SelectItem value="complete">Complete Package</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="budgetRange">Budget Range</Label>
                <Select 
                  value={formData.budgetRange}
                  onValueChange={(value) => handleSelectChange('budgetRange', value)}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select your budget range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="under50k">Under ₹50,000</SelectItem>
                    <SelectItem value="50k-1l">₹50,000 - ₹1,00,000</SelectItem>
                    <SelectItem value="1l-3l">₹1,00,000 - ₹3,00,000</SelectItem>
                    <SelectItem value="3l-5l">₹3,00,000 - ₹5,00,000</SelectItem>
                    <SelectItem value="above5l">Above ₹5,00,000</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="message">Your Message *</Label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Tell us about your advertising needs..."
                  rows={4}
                  className="mt-2"
                />
              </div>

              {/* Human verification via Cloudflare Turnstile */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <ShieldCheck className="text-blue-600" size={18} />
                  Human Verification
                </div>
                <Turnstile
                  ref={turnstileRef}
                  siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
                  action="contact_form"
                  onVerify={(token) => {
                    setTurnstileToken(token);
                    setTurnstileError(false);
                  }}
                  onError={() => {
                    setTurnstileToken('');
                    setTurnstileError(true);
                    toast.error('Security check failed. Please try again.', { duration: 5000 });
                  }}
                  onExpire={() => {
                    setTurnstileToken('');
                    setTurnstileError(true);
                    toast.error('Security check expired. Please verify again.', { duration: 5000 });
                  }}
                />
                {turnstileError && (
                  <p className="text-sm text-red-600">
                    Please complete the security check to submit your inquiry.
                  </p>
                )}
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg"
              >
                {isSubmitting ? (
                  "Sending..."
                ) : (
                  <>
                    Send Inquiry
                    <Send className="ml-2" size={20} />
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
